import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Printer, DollarSign, Calendar } from 'lucide-react';
import { calculatePaymentBreakdown } from '../utils/shiftCalculations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CashPickupDialog({ open, onClose, shift }) {
  const [businessProfile, setBusinessProfile] = useState(null);
  const [receiver, setReceiver] = useState('');
  const [loading, setLoading] = useState(true);
  const [calculatedCash, setCalculatedCash] = useState(0);
  const printRef = useRef(null);

  useEffect(() => {
    if (open && shift) {
      fetchBusinessProfile();
      fetchAndCalculate();
    }
  }, [open, shift]);

  const fetchBusinessProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/business-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBusinessProfile(response.data.profile);
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  const fetchAndCalculate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // CRITICAL: EXACTLY SAME as ShiftReportDialog untuk consistency!
      const startDate = new Date(shift.start_time);
      const endDate = shift.end_time ? new Date(shift.end_time) : new Date();
      const shiftBranchId = shift.branch_id;
      
      console.log('[CashPickup] CODE VERSION: 2025-01-13 - EXACT SAME AS ShiftReportDialog');
      console.log('[CashPickup] Fetching with:', {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        branch_id: shiftBranchId,
        cashier_username: shift.username,
        use_shift_time: true
      });
      
      const response = await axios.get(
        `${API_URL}/api/transactions/unified`,
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          params: {
            date_filter: 'custom',
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
            type_filter: 'all',
            branch_id: shiftBranchId,
            cashier_username: shift.username,
            use_shift_time: true,
            only_paid: true,
            limit: 1000
          }
        }
      );
      
      const transactions = response.data.transactions || [];
      
      // Filter PERSIS seperti ShiftReportDialog (shiftBranchId sudah dideclare di atas)
      const filteredTransactions = transactions.filter(t => {
        if (t.payment_status !== 'paid') return false;
        if (t.branch_id !== shiftBranchId) return false;
        return true;
      });
      
      // Calculate payment breakdown menggunakan shared utility
      const paymentBreakdown = calculatePaymentBreakdown(filteredTransactions);
      
      console.log('[CashPickup] ===== WITH CORRECT PARAMETERS =====');
      console.log('[CashPickup] Transactions:', filteredTransactions.length);
      console.log('[CashPickup] Payment breakdown:', paymentBreakdown);
      console.log('[CashPickup] Cash (SEHARUSNYA):', paymentBreakdown.cash);
      console.log('[CashPickup] =============================================');
      
      setCalculatedCash(paymentBreakdown.cash);
    } catch (error) {
      console.error('[CashPickup] Error:', error);
      toast.error('Gagal memuat data transaksi');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!receiver.trim()) {
      toast.error('Nama penerima wajib diisi');
      return;
    }

    const printWindow = window.open('', '_blank');
    const content = printRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bukti Pengambilan Uang - ${shift?.branch || ''}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: A4;
              margin: 10mm 15mm;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              font-size: 10pt;
              line-height: 1.3;
              color: #000;
              padding: 10px;
            }
            
            .header {
              text-align: center;
              border-bottom: 2px double #000;
              padding-bottom: 8px;
              margin-bottom: 10px;
            }
            
            .header h1 {
              font-size: 14pt;
              font-weight: bold;
              margin-bottom: 3px;
              text-transform: uppercase;
            }
            
            .header .subtitle {
              font-size: 9pt;
              color: #333;
              margin: 2px 0;
            }
            
            .info-section {
              margin: 10px 0;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              padding: 4px 0;
              border-bottom: 1px solid #ddd;
            }
            
            .info-label {
              font-weight: bold;
              width: 40%;
              font-size: 9pt;
            }
            
            .info-value {
              width: 60%;
              text-align: right;
              font-size: 9pt;
            }
            
            .total-box {
              background: #f0f0f0;
              border: 2px solid #000;
              padding: 10px;
              margin: 12px 0;
              text-align: center;
            }
            
            .total-label {
              font-size: 9pt;
              font-weight: bold;
              margin-bottom: 4px;
            }
            
            .total-amount {
              font-size: 18pt;
              font-weight: bold;
              color: #000;
            }
            
            .handover-fields {
              margin: 12px 0 !important;
              padding-top: 10px !important;
              font-size: 9pt;
            }
            
            .signature-section {
              margin-top: 15px;
              display: flex;
              justify-content: space-between;
            }
            
            .signature-box {
              width: 45%;
              text-align: center;
            }
            
            .signature-label {
              font-weight: bold;
              margin-bottom: 3px;
              font-size: 9pt;
            }
            
            .signature-name {
              margin-bottom: 35px;
              font-size: 9pt;
            }
            
            .signature-line {
              border-top: 1px solid #000;
              padding-top: 3px;
              font-size: 9pt;
            }
            
            .footer {
              margin-top: 12px;
              padding-top: 8px;
              border-top: 1px solid #ddd;
              text-align: center;
              font-size: 8pt;
              color: #666;
            }
            
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (!open || !shift) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jakarta'
    }).format(date);
  };

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Use CALCULATED cash from payment breakdown (SEHARUSNYA)
  const startingCash = shift.starting_cash || 0;
  const expectedCash = startingCash + calculatedCash;
  const totalCash = expectedCash;
  
  // Shift name = nama kasir yang kerja
  const shiftName = shift.full_name || shift.username || '-';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Bukti Pengambilan Uang</h2>
              <p className="text-sm text-gray-500">Shift: {shift.shift_name || 'N/A'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-6">
          {/* Input Penerima */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nama Penerima *
            </label>
            <input
              type="text"
              value={receiver}
              onChange={(e) => setReceiver(e.target.value)}
              placeholder="Masukkan nama penerima (Owner/Karyawan)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            <p className="text-xs text-gray-500 mt-2">
              * Isi dengan nama yang menerima uang (bisa Owner atau Karyawan)
            </p>
          </div>

          {/* Print Preview */}
          <div ref={printRef} className="bg-white border-2 border-gray-300 rounded-lg p-8">
            <div className="header">
              <h1>{businessProfile?.business_name || 'BUSINESS NAME'}</h1>
              <div className="subtitle">{businessProfile?.address || ''}</div>
              <div className="subtitle">{businessProfile?.phone || ''}</div>
              <div style={{ marginTop: '15px', fontSize: '16pt', fontWeight: 'bold' }}>
                BUKTI PENGAMBILAN UANG
              </div>
            </div>

            <div className="info-section">
              <div className="info-row">
                <div className="info-label">Tanggal Pengambilan:</div>
                <div className="info-value">{new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
              </div>
              <div className="info-row">
                <div className="info-label">Waktu:</div>
                <div className="info-value">{new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' })} WIB</div>
              </div>
              <div className="info-row">
                <div className="info-label">Cabang:</div>
                <div className="info-value">{shift.branch || '-'}</div>
              </div>
              <div className="info-row">
                <div className="info-label">Shift:</div>
                <div className="info-value">{shiftName}</div>
              </div>
              <div className="info-row">
                <div className="info-label">Periode Shift:</div>
                <div className="info-value">
                  {formatDateTime(shift.start_time)} - {formatDateTime(shift.end_time)}
                </div>
              </div>
            </div>

            {/* Total Box - TETAP ADA */}
            <div className="total-box">
              <div className="total-label">TOTAL UANG YANG DIAMBIL</div>
              <div className="total-amount">{formatCurrency(totalCash)}</div>
              <div style={{ fontSize: '10pt', marginTop: '8px', color: '#666' }}>
                (Total Kas Rekonsiliasi)
              </div>
            </div>

            {/* Kolom Serah Terima - untuk diisi manual */}
            <div className="handover-fields" style={{ margin: '12px 0', borderTop: '1px solid #ccc', paddingTop: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dotted #999', fontSize: '10pt' }}>
                <span style={{ fontWeight: 'bold' }}>Uang yang Diserahkan:</span>
                <span>Rp _______________________</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dotted #999', fontSize: '10pt' }}>
                <span style={{ fontWeight: 'bold' }}>Selisih:</span>
                <span>Rp _______________________</span>
              </div>
              <div style={{ padding: '6px 0', fontSize: '10pt' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Alasan Selisih:</div>
                <div style={{ borderBottom: '1px solid #999', height: '18px', marginBottom: '3px' }}></div>
                <div style={{ borderBottom: '1px solid #999', height: '18px' }}></div>
              </div>
            </div>

            <div className="signature-section">
              <div className="signature-box">
                <div className="signature-label">Yang Menyerahkan</div>
                <div className="signature-name">_______________</div>
                <div className="signature-line">
                  _______________
                </div>
              </div>
              <div className="signature-box">
                <div className="signature-label">Yang Menerima</div>
                <div className="signature-name">{receiver || '_______________'}</div>
                <div className="signature-line">
                  {receiver || '_______________'}
                </div>
              </div>
            </div>

            <div className="footer">
              <div>Dicetak oleh: {currentUser.full_name || currentUser.username || '-'}</div>
              <div>Tanggal cetak: {new Date().toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' })} WIB</div>
              <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                Dokumen ini adalah bukti sah pengambilan uang
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Batal
          </button>
          <button
            onClick={handlePrint}
            disabled={!receiver.trim() || loading}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
          >
            <Printer className="h-5 w-5" />
            {loading ? 'Menghitung...' : 'Cetak Bukti'}
          </button>
        </div>
      </div>
    </div>
  );
}
